// 8-bit stamp glyphs for the Pixel Pass. Each glyph is an 8x8 pixel map
// rendered as SVG rects (crispEdges keeps them chunky at any size).
// Drawn in currentColor so the caller controls the palette via CSS.

const MAPS: Record<string, string[]> = {
  bean: [
    '..####..',
    '.######.',
    '##.#####',
    '###.####',
    '####.###',
    '#####.##',
    '.######.',
    '..####..',
  ],
  mug: [
    '........',
    '######..',
    '######.#',
    '######.#',
    '######.#',
    '######..',
    '.####...',
    '........',
  ],
  croissant: [
    '..####..',
    '.##..##.',
    '##....##',
    '##......',
    '##......',
    '##....##',
    '.##..##.',
    '..####..',
  ],
  portafilter: [
    '........',
    '.######.',
    '.######.',
    '..####..',
    '...##...',
    '...#####',
    '........',
    '........',
  ],
  tulip: [
    '..####..',
    '.######.',
    '..####..',
    '...##...',
    '..####..',
    '.######.',
    '...##...',
    '..####..',
  ],
  donut: [
    '..####..',
    '.######.',
    '###..###',
    '##....##',
    '##....##',
    '###..###',
    '.######.',
    '..####..',
  ],
  star: [
    '...##...',
    '...##...',
    '.######.',
    '########',
    '.######.',
    '..####..',
    '.#....#.',
    '........',
  ],
  'skull-mug': [
    '.######.',
    '########',
    '##.##.##',
    '########',
    '########',
    '.#.##.#.',
    '.######.',
    '..#..#..',
  ],
  // Butcher set (migration 036) — in the picker for every shop; the butcher
  // vertical defaults to these.
  sausage: [
    '........',
    '.##.....',
    '########',
    '##.###.#',
    '#.###.##',
    '########',
    '.....##.',
    '........',
  ],
  cleaver: [
    '.######.',
    '.######.',
    '.######.',
    '.######.',
    '..####..',
    '...##...',
    '...##...',
    '...#....',
  ],
  steak: [
    '..####..',
    '.######.',
    '########',
    '###..###',
    '###.####',
    '.######.',
    '..####..',
    '........',
  ],
};

export const STAMP_PRESET_IDS = Object.keys(MAPS);

export function StampGlyph({ icon, className = '' }: { icon: string; className?: string }) {
  const map = MAPS[icon];
  if (!map) return null;
  const cells: JSX.Element[] = [];
  map.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      if (row[x] === '#') {
        cells.push(<rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} />);
      }
    }
  });
  return (
    <svg
      role="img"
      aria-label={icon}
      viewBox="0 0 8 8"
      className={className}
      fill="currentColor"
      shapeRendering="crispEdges"
    >
      {cells}
    </svg>
  );
}

// Fill for one filled stamp-card cell — shared by PixelPass (the full pass)
// and StampCard (the home-screen summary) so the preset/logo/default
// branching lives in exactly one place.
export function StampCell({
  icon,
  accentColor,
  shopPixelImageUrl,
  shopName,
}: {
  icon: string;
  accentColor?: string | null;
  shopPixelImageUrl?: string; // used when icon === 'logo'
  shopName?: string; // alt text for the logo image
}) {
  const isLogo = icon === 'logo' && !!shopPixelImageUrl;
  const isPreset = STAMP_PRESET_IDS.includes(icon);

  if (isLogo) {
    return (
      <div className="w-full h-full bg-white flex items-center justify-center">
        <img
          src={shopPixelImageUrl}
          alt={`${shopName || 'shop'} stamp`}
          className="w-full h-full object-contain"
          style={{ imageRendering: 'pixelated' }}
        />
      </div>
    );
  }
  if (isPreset) {
    // Coral language: gold stamp fill, ink glyph (mockup 5a); the shop's
    // storefront accent still tints the glyph when one is set.
    return (
      <div
        className="w-full h-full bg-accent-alt flex items-center justify-center text-primary p-0.5"
        style={accentColor ? { color: accentColor } : undefined}
      >
        <StampGlyph icon={icon} className="w-full h-full" />
      </div>
    );
  }
  return (
    <div
      className="w-full h-full bg-accent-alt"
      style={accentColor ? { background: accentColor } : undefined}
    />
  );
}
